import { ThreatClass, type ThreatClassId } from '../config/threats';
import { TargetKind } from '../config/targets';
import { GAUNTLET } from '../config/tuning';
import { Rng } from '../core/Rng';
import { validateMission, type Mission } from './schema';

/**
 * The two non-campaign modes (SPEC §3.10.2): Free Flight and Gauntlet.
 *
 * Both are built as ordinary mission objects and pushed through the same validator as the authored
 * JSON. That is the point of generating them rather than special-casing them: if a generator can
 * emit a mission the loader rejects, the bug surfaces in a test rather than as a wave that will not
 * spawn. A generated mission is not a privileged mission.
 *
 * Distances in metres, angles in degrees at this boundary.
 */

export interface FreeFlightConfig {
  /** 0–1. Scales how many sites appear and how good their crews are. */
  readonly threatDensity: number;
  readonly timeOfDay: number;
  readonly windSpeedMs: number;
  readonly turbulence: number;
  readonly seed: number;
}

export const FREE_FLIGHT_DEFAULT: FreeFlightConfig = {
  threatDensity: 0.35,
  timeOfDay: 12,
  windSpeedMs: 3,
  turbulence: 0.2,
  seed: 0x5eed0f11,
};

/** Candidate emplacements, drawn from real features of the theatre so nothing lands in the sea. */
const SITE_POSITIONS: readonly (readonly [number, number, number])[] = [
  [4250, 0, 800],
  [4600, 0, 2400],
  [2600, 0, -1200],
  [6400, 0, 1500],
  [10500, 0, -600],
  [7200, 0, 2600],
  [5200, 0, 1150],
  [3900, 0, 1600],
  [8200, 0, 900],
  [1800, 0, 2100],
];

const SITE_TYPES: readonly ThreatClassId[] = [
  ThreatClass.ShoradTracked,
  ThreatClass.ShoradGun,
  ThreatClass.Manpads,
  ThreatClass.MediumSam,
  ThreatClass.EwJammer,
];

/**
 * Free Flight: the theatre with a threat slider and no objective but the one you fly to.
 *
 * The target is fixed (the command post) so the mode still has a terminal event — a one-way drone
 * with nothing to hit has no ending, and an ending is what makes a sandbox a flight rather than a
 * screensaver.
 */
export function buildFreeFlight(config: FreeFlightConfig = FREE_FLIGHT_DEFAULT): Mission {
  const rng = new Rng(config.seed);
  const random = (): number => rng.next();
  const density = clamp01(config.threatDensity);
  const siteCount = Math.round(density * SITE_POSITIONS.length);

  const threats = [];
  for (let i = 0; i < siteCount; i += 1) {
    const position = SITE_POSITIONS[i];
    const type = SITE_TYPES[Math.floor(random() * SITE_TYPES.length) % SITE_TYPES.length];
    threats.push({
      id: `FF-${i + 1}`,
      type,
      position,
      active: true,
      overrides: { crewQuality: round2(0.4 + 0.5 * density) },
    });
  }

  return validateMission({
    id: 'free-flight',
    title: 'Free Flight',
    order: 900,
    theatre: 'kerani',
    brief: {
      summary: 'The theatre, configured to taste. Fly it however you like.',
      intel: ['Threat density, weather and time of day are yours to set.'],
    },
    environment: {
      timeOfDay: config.timeOfDay,
      windDirDeg: 240,
      windSpeedMs: config.windSpeedMs,
      turbulence: clamp01(config.turbulence),
      visibilityKm: 24,
      cloudCover: 0.3,
      seaState: 2,
    },
    launch: { position: [-6200, 450, 2200], headingDeg: 90, altitudeM: 450, airspeedMs: 85 },
    airframe: 'SCOUT',
    loadout: {
      allowed: ['FRAGMENTATION', 'PENETRATOR'],
      default: ['FRAGMENTATION'],
      chaff: 20,
      flares: 20,
      warhead: 'FRAGMENTATION',
    },
    route: [
      { id: 'wp1', position: [0, 200, 1800], label: 'MIDPOINT', type: 'nav' },
      { id: 'wp2', position: [4320, 40, 1320], label: 'TARGET', type: 'target' },
    ],
    threats,
    targets: [
      {
        id: 'FF-TARGET',
        type: TargetKind.CommandPost,
        position: [4320, 0, 1320],
        headingDeg: 30,
        label: 'COMMAND POST',
      },
    ],
    objectives: [
      {
        id: 'ff-strike',
        kind: 'destroy',
        targetRef: 'FF-TARGET',
        primary: true,
        description: 'Destroy the command post',
        weight: 100,
      },
    ],
    events: [],
    scoring: {
      parTimeS: 420,
      weights: { objectives: 500, accuracy: 200, evasion: 150, altitude: 100, time: 50 },
      expertRequires: [],
    },
  });
}

/**
 * Gauntlet wave `n`: endless, escalating (SPEC §3.10.2).
 *
 * Escalation adds sites and crew quality rather than health bars, because the difficulty of this
 * game is the density of the problem, not the toughness of any one piece of it. Wave one is a
 * single battery; by wave ten the theatre is saturated.
 */
export function buildGauntletWave(wave: number, seed = GAUNTLET.seed): Mission {
  const index = Math.max(1, Math.floor(wave));
  // Vary the layout per wave from a hash of the wave number, so a wave is reproducible but the
  // sequence is not a fixed script the player can memorise.
  const rng = new Rng((seed ^ Math.imul(index, 0x9e3779b1)) >>> 0);
  const random = (): number => rng.next();

  const siteCount = Math.min(SITE_POSITIONS.length, GAUNTLET.baseSites + Math.floor(index / 2));
  const crewQuality = Math.min(1, GAUNTLET.baseCrewQuality + index * GAUNTLET.crewQualityPerWave);
  const magazine = GAUNTLET.baseMagazine + Math.floor(index / GAUNTLET.wavesPerReload);

  const order = shuffled(SITE_POSITIONS.length, random);
  const threats = [];
  for (let i = 0; i < siteCount; i += 1) {
    const position = SITE_POSITIONS[order[i]];
    const type = SITE_TYPES[Math.floor(random() * SITE_TYPES.length) % SITE_TYPES.length];
    threats.push({
      id: `G${index}-${i + 1}`,
      type,
      position,
      active: true,
      overrides: { crewQuality: round2(crewQuality), magazine },
    });
  }

  return validateMission({
    id: `gauntlet-${index}`,
    title: `Gauntlet — Wave ${index}`,
    order: 1000 + index,
    theatre: 'kerani',
    brief: {
      summary: `Wave ${index}. It does not stop.`,
      intel: [`${siteCount} sites active.`, `Crews at ${Math.round(crewQuality * 100)}% competence.`],
    },
    environment: {
      timeOfDay: 6 + (index % 12),
      windDirDeg: 240,
      windSpeedMs: 3 + (index % 5),
      turbulence: Math.min(1, 0.2 + index * 0.03),
      visibilityKm: Math.max(8, 24 - index),
      cloudCover: Math.min(1, 0.2 + index * 0.05),
      seaState: Math.min(5, 1 + Math.floor(index / 3)),
    },
    launch: { position: [-6200, 400, 2200], headingDeg: 90, altitudeM: 400, airspeedMs: 90 },
    airframe: 'SCOUT',
    loadout: {
      allowed: ['FRAGMENTATION', 'PENETRATOR'],
      default: ['FRAGMENTATION'],
      chaff: 12,
      flares: 12,
      warhead: 'FRAGMENTATION',
    },
    route: [{ id: 'wp1', position: [4320, 40, 1320], label: 'TARGET', type: 'target' }],
    threats,
    targets: [
      {
        id: `G${index}-TARGET`,
        type: TargetKind.CommandPost,
        position: [4320, 0, 1320],
        label: 'COMMAND POST',
      },
    ],
    objectives: [
      {
        id: 'g-strike',
        kind: 'destroy',
        targetRef: `G${index}-TARGET`,
        primary: true,
        description: 'Destroy the command post',
        weight: 70,
      },
      {
        id: 'g-survive',
        kind: 'survive',
        seconds: 120,
        primary: false,
        description: 'Survive two minutes',
        weight: 30,
      },
    ],
    events: [],
    scoring: {
      parTimeS: 360,
      weights: { objectives: 400, accuracy: 150, evasion: 250, altitude: 150, time: 50 },
      expertRequires: [],
    },
  });
}

/** Fisher–Yates over indices, from a seeded stream — never `Math.random` (CLAUDE.md). */
function shuffled(count: number, random: () => number): number[] {
  const order: number[] = [];
  for (let i = 0; i < count; i += 1) order.push(i);
  for (let i = count - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const swap = order[i];
    order[i] = order[j];
    order[j] = swap;
  }
  return order;
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
